import { notificationsApi } from '@/lib/api/notifications.api'
import { z } from 'zod'

import type {
  BulkUpdateNotificationPreferencesDto,
  NotificationPreference,
  UpdateNotificationPreferenceDto,
} from '@scrambl/shared'
import {
  isValidUUID,
  NotificationChannel,
  NotificationStatus,
  NotificationType,
} from '@scrambl/shared'

import type { NotificationReaderContext } from './reads.api'
import {
  assertNotificationCount,
  assertNotificationReaderContext,
  assertOwnedNotification,
} from './reads.api'

const preferenceInput: z.ZodType<
  UpdateNotificationPreferenceDto,
  z.ZodTypeDef,
  unknown
> = z
  .object({
    channel: z.nativeEnum(NotificationChannel),
    type: z.nativeEnum(NotificationType),
    enabled: z.boolean(),
  })
  .strict()
  .transform(row => ({
    channel: row.channel,
    type: row.type,
    enabled: row.enabled,
  }))
export const notificationPreferenceBatchInput: z.ZodType<
  BulkUpdateNotificationPreferencesDto,
  z.ZodTypeDef,
  unknown
> = z
  .object({ preferences: z.array(preferenceInput).min(1) })
  .strict()
  .transform(row => ({ preferences: row.preferences }))
export class NotificationCommandContractError extends Error {
  constructor() {
    super('Notification change could not be confirmed.')
    this.name = 'NotificationCommandContractError'
  }
}
function target(id: string, context: NotificationReaderContext) {
  assertNotificationReaderContext(context)
  if (!isValidUUID(id)) throw new NotificationCommandContractError()
}
function targets(ids: string[], context: NotificationReaderContext) {
  assertNotificationReaderContext(context)
  if (!Array.isArray(ids) || !ids.length || !ids.every(isValidUUID))
    throw new NotificationCommandContractError()
  return [...new Set(ids)]
}
function confirmCount(
  value: unknown,
  context: NotificationReaderContext,
  ceiling = Infinity,
) {
  assertNotificationReaderContext(context)
  assertNotificationCount(value)
  if ((value as number) > ceiling) throw new NotificationCommandContractError()
}
function pair(input: { channel: string; type: string }) {
  return JSON.stringify([input.channel, input.type])
}
export function assertNotificationPreference(
  row: NotificationPreference,
  context: NotificationReaderContext,
  expected?: UpdateNotificationPreferenceDto,
) {
  assertNotificationReaderContext(context)
  if (
    !row ||
    !isValidUUID(row.id) ||
    row.userId !== context.userId ||
    row.organizationId !== context.organizationId ||
    !preferenceInput.safeParse({
      channel: row.channel,
      type: row.type,
      enabled: row.enabled,
    }).success ||
    (expected &&
      (pair(row) !== pair(expected) || row.enabled !== expected.enabled))
  )
    throw new NotificationCommandContractError()
}
export function confirmNotificationPreferences(
  reply: { updated: number; preferences: NotificationPreference[] },
  context: NotificationReaderContext,
  expected?: UpdateNotificationPreferenceDto[],
) {
  confirmCount(reply?.updated, context)
  if (
    !reply ||
    !Array.isArray(reply.preferences) ||
    reply.updated !== reply.preferences.length ||
    (expected && reply.updated !== expected.length)
  )
    throw new NotificationCommandContractError()
  const expectedByKey = expected
    ? new Map(expected.map(input => [pair(input), input]))
    : null
  const seen = new Set<string>()
  for (const row of reply.preferences) {
    assertNotificationPreference(row, context)
    const key = pair(row)
    if (seen.has(key) || (expectedByKey && !expectedByKey.has(key)))
      throw new NotificationCommandContractError()
    seen.add(key)
    if (expectedByKey)
      assertNotificationPreference(row, context, expectedByKey.get(key))
  }
  return { updated: reply.updated }
}
export function confirmNotificationPreferenceReset(
  reply: Awaited<ReturnType<typeof notificationsApi.setDefaultPreferences>>,
  context: NotificationReaderContext,
) {
  assertNotificationReaderContext(context)
  if (
    !reply ||
    reply.reset !== true ||
    reply.userId !== context.userId ||
    reply.organizationId !== context.organizationId ||
    reply.updated !== 0 ||
    !Array.isArray(reply.preferences) ||
    reply.preferences.length !== 0
  )
    throw new NotificationCommandContractError()
  assertNotificationCount(reply.removed)
  return { updated: 0, removed: reply.removed }
}
export const notificationCommandsApi = {
  async read(id: string, context: NotificationReaderContext) {
    target(id, context)
    const row = await notificationsApi.markAsRead(id, {
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    assertOwnedNotification(row, context)
    if (row.id !== id || row.status !== NotificationStatus.READ)
      throw new NotificationCommandContractError()
    return { id, status: row.status }
  },
  async readMany(ids: string[], context: NotificationReaderContext) {
    const captured = targets(ids, context)
    const reply = await notificationsApi.markMultipleAsRead(captured, {
      signal: context.signal,
    })
    confirmCount(reply?.updated, context, captured.length)
    return { updated: reply.updated }
  },
  async readAll(_input: void, context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    const reply = await notificationsApi.markAllAsRead({
      signal: context.signal,
    })
    confirmCount(reply?.updated, context)
    return { updated: reply.updated }
  },
  async dismiss(id: string, context: NotificationReaderContext) {
    target(id, context)
    const row = await notificationsApi.dismissNotification(id, {
      signal: context.signal,
    })
    assertNotificationReaderContext(context)
    assertOwnedNotification(row, context)
    if (row.id !== id || row.status !== NotificationStatus.DISMISSED)
      throw new NotificationCommandContractError()
    return { id, status: row.status }
  },
  async dismissMany(ids: string[], context: NotificationReaderContext) {
    const captured = targets(ids, context)
    const reply = await notificationsApi.dismissMultiple(captured, {
      signal: context.signal,
    })
    confirmCount(reply?.updated, context, captured.length)
    return { updated: reply.updated }
  },
  async dismissAll(
    types: string[] | undefined,
    context: NotificationReaderContext,
  ) {
    assertNotificationReaderContext(context)
    if (
      types !== undefined &&
      (!Array.isArray(types) ||
        !types.length ||
        !types.every(type =>
          Object.values(NotificationType).includes(type as NotificationType),
        ))
    )
      throw new NotificationCommandContractError()
    const reply = await notificationsApi.dismissAll(
      types ? [...new Set(types)] : undefined,
      { signal: context.signal },
    )
    confirmCount(reply?.dismissed, context)
    return { dismissed: reply.dismissed }
  },
  async preference(
    input: UpdateNotificationPreferenceDto,
    context: NotificationReaderContext,
  ) {
    assertNotificationReaderContext(context)
    const parsed = preferenceInput.safeParse(input)
    if (!parsed.success) throw new NotificationCommandContractError()
    const row = await notificationsApi.updatePreference(parsed.data, {
      signal: context.signal,
    })
    assertNotificationPreference(row, context, parsed.data)
    return { channel: row.channel, type: row.type, enabled: row.enabled }
  },
  async preferences(
    input: BulkUpdateNotificationPreferencesDto,
    context: NotificationReaderContext,
  ) {
    assertNotificationReaderContext(context)
    const parsed = notificationPreferenceBatchInput.safeParse(input)
    if (
      !parsed.success ||
      new Set(parsed.data.preferences.map(pair)).size !==
        parsed.data.preferences.length
    )
      throw new NotificationCommandContractError()
    return confirmNotificationPreferences(
      await notificationsApi.updateMultiplePreferences(parsed.data, {
        signal: context.signal,
      }),
      context,
      parsed.data.preferences,
    )
  },
  async defaults(_input: void, context: NotificationReaderContext) {
    assertNotificationReaderContext(context)
    return confirmNotificationPreferenceReset(
      await notificationsApi.setDefaultPreferences({ signal: context.signal }),
      context,
    )
  },
}
